import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  User,
  Mail,
  Phone,
  MapPin, Edit,
  Save,
  X,
  Camera,
  BookOpen,
  Award,
  Lock,
  LogOut,
  Key,
  Eye,
  EyeOff,
  Shield,
  Users,
  BarChart3,
  Download
} from "lucide-react"
import { useAuth } from "../../hooks/useAuth"
// Hook useUserData supprimé - données mockées utilisées directement
import { useGet, usePut } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { showErrorToast, showSuccessToast } from "../../utils/toast"
import { api } from "../../utils/apiComponent/apiClient"
import MyCoursesSection from "../../components/MyCoursesSection"
import { useCertificates } from "../../hooks/useCertificates"
import { useCourseProgress } from "../../hooks/useCourseProgress"
import { useTranslation } from "../../i18n"
import { usePatch } from "../../utils/apiComponent/useApi"
import ImageUpload from "../../components/ImageUpload"
import { CertificatePreview } from "../../components/CertificatePreview"
import { CertificateSkeleton } from "../../components/skeletons"
import Avatar from "../../components/Avatar"

export default function ProfileUserPage() {
  const { tObj, language, changeLanguage } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, logout, loading: authLoading } = useAuth()
  const { loading: updatingLanguage } = usePatch(endpoints.updatePreferredLanguage)
  const [isEditing, setIsEditing] = useState(false)
  const [activeTab, setActiveTab] = useState("profile")
  const [showPassword, setShowPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  // Gérer le paramètre tab dans l'URL
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam && ['profile', 'password', 'courses', 'achievements', 'admin'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [searchParams])
  
  // Récupération des données utilisateur avec le hook personnalisé
  // Données mockées pour le développement
  const userData = {
    id: "1",
    name: "John Doe",
    email: "john@example.com",
    avatar: "https://via.placeholder.com/150",
    phone: "+33123456789",
    address: "123 Rue de la Paix",
    city: "Paris",
    country: "France",
    bio: "Développeur passionné par les nouvelles technologies",
    website: "https://johndoe.com",
    createdAt: "2024-01-01T00:00:00.000Z"
  }
  
  // Récupérer les cours avec progression depuis l'API
  const { progress: courseProgress, loading: progressLoading } = useCourseProgress()
  
  // Récupérer les certificats depuis l'API
  const { certificates: apiCertificates, loading: certificatesLoading } = useCertificates()
  
  // Calculer les statistiques à partir des données de l'API
  const courses = courseProgress?.map(progress => ({
    id: progress.course?.id,
    enrollmentStatus: progress.completedAt ? "COMPLETED" : (progress.progress > 0 ? "IN_PROGRESS" : "ENROLLED"),
    progress: progress.progress || 0
  })) || []
  
  const userStats = {
    totalCourses: courses.length,
    completedCourses: courses.filter(c => c.enrollmentStatus === "COMPLETED").length,
    inProgressCourses: courses.filter(c => c.enrollmentStatus === "IN_PROGRESS").length,
    totalHours: 0, // Peut être calculé depuis course.duration si nécessaire
    certificates: apiCertificates?.length || 0
  }
  
  // Transformer les données de l'API pour correspondre au format attendu
  const certificates = apiCertificates?.map(cert => ({
    id: cert.id,
    courseTitle: cert.course?.title || "Cours terminé",
    courseThumbnail: cert.course?.thumbnail || "/placeholder.svg",
    issuedAt: cert.issuedAt,
    certificateNumber: cert.certificateNumber,
    pdfUrl: cert.pdfUrl,
    course: cert.course,
    enrollment: cert.enrollment,
    // Inclure les champs d'évaluation depuis l'API
    hasRated: cert.hasRated ?? false,
    hasCommented: cert.hasCommented ?? false,
    canDownloadCertificate: cert.canDownloadCertificate ?? true
  })) || []
  
  // Données mockées pour le développement (si l'API ne retourne rien)
  const mockCertificates = [
    {
      id: "1",
      courseTitle: "Introduction à React",
      courseId: "1",
      issuedAt: "2024-01-15T10:30:00.000Z",
      certificateUrl: "https://example.com/certificates/cert-1.pdf",
      courseThumbnail: "https://via.placeholder.com/300x200"
    },
    {
      id: "2",
      courseTitle: "JavaScript Avancé",
      courseId: "2",
      issuedAt: "2024-01-20T14:20:00.000Z",
      certificateUrl: "https://example.com/certificates/cert-2.pdf",
      courseThumbnail: "https://via.placeholder.com/300x200"
    }
  ]
  
  const [notifications, setNotifications] = useState([
    {
      id: "1",
      title: "Nouveau cours disponible",
      message: "Le cours 'Node.js pour débutants' est maintenant disponible",
      type: "info",
      read: false,
      createdAt: "2024-01-25T09:00:00.000Z"
    },
    {
      id: "2",
      title: "Rappel de cours",
      message: "N'oubliez pas de continuer votre cours 'Introduction à React'",
      type: "reminder",
      read: true,
      createdAt: "2024-01-24T16:30:00.000Z"
    }
  ])
  
  const userDataLoading = false
  
  // Récupération des données utilisateur de base (pour l'édition)
  const { data: userDataForEdit, loading: userLoading, execute: fetchUser } = useGet(endpoints.user, { immediate: false })
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    country: ""
  })

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  })

  // État pour l'avatar
  const [avatarFile, setAvatarFile] = useState(null)
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar || userDataForEdit?.avatar || null)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)

  // Mise à jour des données utilisateur
  const { execute: updateUser, loading: updateLoading } = usePatch(endpoints.user)
  const { execute: updatePassword, loading: passwordLoading } = usePatch(`${endpoints.user}/password`)

  // Les données sont maintenant définies directement dans le composant

  // Charger les données utilisateur depuis l'API ou localStorage
  useEffect(() => {
    if (userDataForEdit) {
      setFormData({
        name: userDataForEdit.name || "",
        email: userDataForEdit.email || "",
        phone: userDataForEdit.phone || "",
        address: userDataForEdit.address || "",
        city: userDataForEdit.city || "",
        country: userDataForEdit.country || ""
      })
      // Mettre à jour l'avatar si disponible
      if (userDataForEdit.avatar) {
        setAvatarUrl(userDataForEdit.avatar)
      }
    } else if (user) {
      // Fallback vers localStorage si l'API n'a pas encore répondu
      const storedUserData = localStorage.getItem('userData')
      if (storedUserData) {
        const parsedData = JSON.parse(storedUserData)
        setFormData({
          name: parsedData.name || "",
          email: parsedData.email || "",
          phone: parsedData.phone || "",
          address: parsedData.address || "",
          city: parsedData.city || "",
          country: parsedData.country || ""
        })
        if (parsedData.avatar) {
          setAvatarUrl(parsedData.avatar)
        }
      }
      // Utiliser l'avatar de l'utilisateur connecté
      if (user.avatar) {
        setAvatarUrl(user.avatar)
      }
    }
  }, [userDataForEdit, user])

  // Vérification d'authentification
  useEffect(() => {
    // Ne rediriger que si l'authentification a fini de charger ET qu'il n'y a pas d'utilisateur
    if (!authLoading && !user) {
      console.log("Authentication loaded but no user found, redirecting to login")
      navigate('/login')
    } else if (user) {
    }
  }, [user, authLoading, navigate])

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSave = async () => {
    try {
      await updateUser(formData)
      showSuccessToast({ message: tObj.profile.profileUpdated() })
      setIsEditing(false)
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error)
      const errorMessage = error?.response?.data?.message || error?.message || tObj.profile.profileUpdateError()
      showErrorToast({ message: errorMessage })
    }
  }

  const handleCancel = () => {
    // Recharger les données originales
    if (userDataForEdit) {
      setFormData({
        name: userDataForEdit.name || "",
        email: userDataForEdit.email || "",
        phone: userDataForEdit.phone || "",
        address: userDataForEdit.address || "",
        city: userDataForEdit.city || "",
        country: userDataForEdit.country || ""
      })
    }
    setIsEditing(false)
  }

  const handlePasswordUpdate = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showErrorToast({ message: tObj.profile.passwordMismatch() })
      return
    }

    if (passwordData.newPassword.length < 6) {
      showErrorToast({ message: tObj.profile.passwordTooShort() })
      return
    }

    try {
      await updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
        confirmNewPassword: passwordData.confirmPassword
      })
      showSuccessToast({ message: tObj.profile.passwordUpdated() })
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      })
    } catch (error) {
      console.error("Erreur lors de la mise à jour du mot de passe:", error)
      
      // Extraire le message d'erreur du backend
      let errorMessage = tObj.profile.passwordUpdateError()
      
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Si c'est une erreur de validation avec un tableau d'erreurs
        errorMessage = error.response.data.errors.join(', ')
      } else if (error?.response?.data?.message) {
        // Si c'est un message d'erreur simple
        errorMessage = error.response.data.message
      } else if (error?.message) {
        // Si c'est une erreur JavaScript
        errorMessage = error.message
      }
      
      showErrorToast({ message: errorMessage })
    }
  }

  // Fonction pour uploader l'image via Cloudinary
  const uploadImageToServer = async (file) => {
    const formData = new FormData()
    // Le champ doit s'appeler 'file' selon la documentation
    formData.append('file', file)
    // Optionnel: spécifier le dossier de destination
    formData.append('folder', 'avatars')
    
    try {
      // Ne pas définir Content-Type manuellement - le navigateur le fait automatiquement avec le boundary
      const response = await api.post(endpoints.cloudinaryUpload, formData)
      
      // L'API retourne : { success: true, data: { url: "...", publicId: "...", ... }, messages: [...] }
      if (response?.success && response?.data?.url) {
        return response.data.url
      } else if (response?.data?.url) {
        // Format alternatif sans success
        return response.data.url
      } else if (response?.url) {
        // Format direct
        return response.url
      } else {
        throw new Error("Format de réponse inattendu de l'API d'upload")
      }
    } catch (error) {
      console.error("Erreur lors de l'upload de l'image:", error)
      // Extraire le message d'erreur
      const errorMessage = error?.response?.data?.message 
        ? (Array.isArray(error.response.data.message) 
            ? error.response.data.message[0] 
            : error.response.data.message)
        : error?.message || "Erreur lors de l'upload de l'image"
      throw new Error(errorMessage)
    }
  }

  // Fonction pour mettre à jour l'avatar
  const handleAvatarUpdate = async () => {
    if (!avatarFile) {
      showErrorToast({ message: "Veuillez sélectionner une image" })
      return
    }

    setUploadingAvatar(true)
    try {
      // 1. Uploader l'image via l'API intermédiaire pour obtenir l'URL
      const imageUrl = await uploadImageToServer(avatarFile)
      
      // 2. Mettre à jour l'avatar avec l'URL obtenue
      await api.patch(endpoints.updateAvatar, { avatar: imageUrl })
      
      // 3. Mettre à jour l'état local
      setAvatarUrl(imageUrl)
      setAvatarFile(null)
      
      // 4. Mettre à jour l'utilisateur dans le contexte et localStorage
      const storedUserData = localStorage.getItem('userData')
      let updatedUser = null
      
      if (storedUserData) {
        const parsedData = JSON.parse(storedUserData)
        parsedData.avatar = imageUrl
        localStorage.setItem('userData', JSON.stringify(parsedData))
        updatedUser = parsedData
      } else if (user) {
        const userData = { ...user, avatar: imageUrl }
        localStorage.setItem('userData', JSON.stringify(userData))
        updatedUser = userData
      }
      
      // Déclencher un événement pour mettre à jour le contexte auth dans tous les composants
      if (updatedUser) {
        window.dispatchEvent(new CustomEvent('authChange', { detail: { user: updatedUser } }))
      }
      
      showSuccessToast({ message: "Photo de profil mise à jour avec succès !" })
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'avatar:", error)
      const errorMessage = error?.response?.data?.message || error?.message || "Erreur lors de la mise à jour de la photo de profil"
      showErrorToast({ message: errorMessage })
    } finally {
      setUploadingAvatar(false)
    }
  }

  const handleDownloadCertificate = async (certificate) => {
    try {
      const certificateId = certificate.id
      
      if (!certificateId) {
        throw new Error(tObj.profile.certificateIdMissing())
      }
      
      // Appeler l'endpoint de téléchargement avec responseType: 'blob'
      // Utiliser api.get comme dans QuizPage.jsx pour la cohérence
      const blob = await api.get(endpoints.downloadCertificate(certificateId), {
        responseType: 'blob'
      })
      
      // Vérifier que blob est valide
      if (!blob || !(blob instanceof Blob)) {
        throw new Error(tObj.profile.invalidFile())
      }
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Générer un nom de fichier avec le titre du cours et la date
      const courseTitle = certificate.courseTitle || certificate.course?.title || 'Certificat'
      const year = certificate.issuedAt ? new Date(certificate.issuedAt).getFullYear() : new Date().getFullYear()
      // Nettoyer le titre pour le nom de fichier (enlever caractères spéciaux)
      const cleanTitle = courseTitle.replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_')
      link.download = `Certificat_${cleanTitle}_${year}.pdf`
      
      // Déclencher le téléchargement
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      showSuccessToast({ message: tObj.profile.certificateDownloadSuccess() })
    } catch (error) {
      console.error("Erreur lors du téléchargement du certificat:", error)
      
      // Fallback : si pdfUrl est disponible, l'ouvrir dans un nouvel onglet
      if (certificate.pdfUrl) {
        window.open(certificate.pdfUrl, '_blank')
        return
      }
      
      const errorMessage = error?.message || tObj.profile.certificateDownloadError()
      showErrorToast({ message: errorMessage })
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  const handleNotificationChange = (field, value) => {
    setNotifications(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Définir les onglets selon le rôle de l'utilisateur
  const getTabs = () => {
    const baseTabs = [
      { id: "profile", name: tObj.profile.profile(), icon: User },
      { id: "password", name: tObj.profile.password(), icon: Lock }
    ]

    // Ajouter les onglets spécifiques aux étudiants
    if (user?.role?.toLowerCase() === 'student' && !user?.isAdmin) {
      return [
        ...baseTabs, // Profil et Mot de passe
        { id: "courses", name: tObj.profile.courses(), icon: BookOpen },
        { id: "achievements", name: tObj.profile.achievements(), icon: Award }
      ]
    }

    // Pour les admins, ajouter un onglet admin et achievements
    if (user?.role?.toLowerCase() === 'admin' || user?.isAdmin) {
      return [
        ...baseTabs, // Profil et Mot de passe
        { id: "admin", name: tObj.profile.administration(), icon: Shield },
        { id: "achievements", name: tObj.profile.achievements(), icon: Award }
      ]
    }

    return baseTabs
  }

  const tabs = getTabs()

  // Afficher un loader pendant le chargement de l'authentification
  if (authLoading || userLoading || userDataLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#141835] mx-auto"></div>
          <p className="mt-4 text-gray-600">{tObj.profile.loadingProfile()}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header amélioré avec style Courses */}
      <div className="relative pt-20 pb-12 sm:pt-24 sm:pb-16 lg:pt-28 lg:pb-20 overflow-hidden">
        {/* Effets de fond animés */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#203a8a]/10 via-blue-50/50 to-purple-50/30"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-400 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl animate-float delay-500"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fade-in-up">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-r from-[#141835] via-[#203a8a] to-[#141835] bg-clip-text text-transparent animate-gradient">
              {tObj.profile.profile()}
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Gérez votre profil et vos préférences
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Header du profil amélioré */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border-0 p-6 sm:p-8 mb-6 sm:mb-8 hover:shadow-2xl transition-all duration-300">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 w-full sm:w-auto">
              <div className="relative group">
                <Avatar
                  src={avatarUrl} 
                  name={formData.name || user?.name} 
                  size={60}
                  className="flex-shrink-0 sm:w-24 sm:h-24 ring-4 ring-[#203a8a]/20 group-hover:ring-[#203a8a]/40 transition-all duration-300"
                />
                {isEditing && (
                  <label 
                    htmlFor="avatar-upload"
                    className="absolute bottom-0 right-0 p-2 bg-gradient-to-br from-[#203a8a] to-[#2563eb] rounded-full shadow-lg hover:scale-110 transition-all duration-300 cursor-pointer border-2 border-white"
                    title="Changer la photo de profil"
                  >
                    <Camera className="w-4 h-4 text-white" />
                    <input
                      id="avatar-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files[0]
                        if (file) {
                          if (file.size > 5 * 1024 * 1024) {
                            showErrorToast({ message: "L'image est trop grande. Veuillez sélectionner une image de moins de 5MB." })
                            return
                          }
                          if (!file.type.startsWith('image/')) {
                            showErrorToast({ message: "Veuillez sélectionner un fichier image valide." })
                            return
                          }
                          setAvatarFile(file)
                        }
                      }}
                    />
                  </label>
                )}
              </div>
              <div className="flex-1 min-w-0 overflow-hidden">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#141835] to-[#203a8a] bg-clip-text text-transparent truncate overflow-hidden text-ellipsis whitespace-nowrap">
                  {formData.name}
                </h1>
                <p className="text-base sm:text-lg text-gray-600 font-medium">
                  {(user?.role?.toLowerCase() === 'admin' || user?.isAdmin) ? tObj.profile.administrator() : tObj.profile.student()}
                </p>
                {(user?.role?.toLowerCase() === 'student' && !user?.isAdmin) && (
                  <div className="flex items-center mt-2 gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                      <BookOpen className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700 font-semibold">
                      {userStats.totalCourses} {tObj.profile.enrolledCourses()}
                    </span>
                  </div>
                )}
                {(user?.role?.toLowerCase() === 'admin' || user?.isAdmin) && (
                  <div className="flex items-center mt-2 gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-[#203a8a] rounded-lg flex items-center justify-center">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-sm sm:text-base text-gray-700 font-semibold">
                      {tObj.profile.adminAccess()}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:gap-3 w-full sm:w-auto">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={updateLoading}
                    className="flex items-center justify-center px-4 sm:px-5 py-2.5 text-sm sm:text-base text-gray-700 hover:text-gray-900 border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 disabled:opacity-50 hover:scale-105 transition-all duration-300 shadow-sm hover:shadow-md"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span>{tObj.profile.cancel()}</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={updateLoading}
                    className="flex items-center justify-center px-4 sm:px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-[#203a8a] to-[#2563eb] text-white rounded-xl hover:from-[#1a1f4a] hover:to-[#1e40af] disabled:opacity-50 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    {updateLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    )}
                    <span>{updateLoading ? tObj.profile.saving() : tObj.profile.save()}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setIsEditing(true)
                      setActiveTab("profile")
                      navigate('/profile?tab=profile', { replace: true })
                    }}
                    className="flex items-center justify-center px-4 sm:px-5 py-2.5 text-sm sm:text-base bg-gradient-to-r from-[#203a8a] to-[#2563eb] text-white rounded-xl hover:from-[#1a1f4a] hover:to-[#1e40af] shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
                  >
                    <Edit className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span>{tObj.profile.edit()}</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center px-4 sm:px-5 py-2.5 text-sm sm:text-base text-red-600 hover:text-red-700 border-2 border-red-300 rounded-xl hover:bg-red-50 hover:border-red-400 shadow-sm hover:shadow-md hover:scale-105 transition-all duration-300"
                  >
                    <LogOut className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span>{tObj.profile.logout()}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {/* Sidebar Navigation améliorée */}
          <div className="lg:col-span-1">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border-0 p-4 sm:p-5 hover:shadow-2xl transition-all duration-300">
              <nav className="space-y-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id)
                        navigate(`/profile?tab=${tab.id}`, { replace: true })
                      }}
                      className={`w-full flex items-center px-3 sm:px-4 py-3 text-sm sm:text-base font-semibold rounded-xl transition-all duration-300 ${
                        activeTab === tab.id
                          ? "bg-gradient-to-r from-[#203a8a] to-[#2563eb] text-white shadow-lg scale-105"
                          : "text-gray-700 hover:text-[#203a8a] hover:bg-gray-100 hover:scale-105"
                      }`}
                    >
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 mr-3 flex-shrink-0" />
                      <span className="truncate">{tab.name}</span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Main Content amélioré */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === "profile" && (
              <div className="space-y-6">
                {/* Personal Information amélioré */}
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border-0 p-6 sm:p-8 hover:shadow-2xl transition-all duration-300">
                  <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#141835] to-[#203a8a] bg-clip-text text-transparent mb-6">
                    {tObj.profile.personalInformation()}
                  </h2>
                  
                  {/* Avatar Upload Section */}
                  {isEditing && (
                    <div className="mb-6 pb-6 border-b border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Photo de profil
                      </label>
                      <div className="flex items-start gap-4">
                        <div className="w-32">
                          <ImageUpload
                            currentImage={avatarFile ? URL.createObjectURL(avatarFile) : avatarUrl}
                            onImageSelect={(file) => {
                              // Vérifier la taille du fichier (max 5MB)
                              if (file.size > 5 * 1024 * 1024) {
                                showErrorToast({ message: "L'image est trop grande. Veuillez sélectionner une image de moins de 5MB." })
                                return
                              }
                              // Vérifier le type de fichier
                              if (!file.type.startsWith('image/')) {
                                showErrorToast({ message: "Veuillez sélectionner un fichier image valide." })
                                return
                              }
                              setAvatarFile(file)
                            }}
                            placeholder="Cliquez pour sélectionner une photo"
                            className="w-full"
                          />
                        </div>
                        <div className="flex-1">
                          {avatarFile && (
                            <div className="space-y-2">
                              <p className="text-sm text-gray-600">
                                Fichier sélectionné : {avatarFile.name} ({(avatarFile.size / 1024).toFixed(2)} KB)
                              </p>
                              <button
                                onClick={handleAvatarUpdate}
                                disabled={uploadingAvatar}
                                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {uploadingAvatar ? (
                                  <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Upload en cours...
                                  </>
                                ) : (
                                  <>
                                    <Camera className="w-4 h-4 mr-2" />
                                    Mettre à jour la photo
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                          {!avatarFile && (
                            <div className="space-y-2">
                              {avatarUrl ? (
                                <p className="text-sm text-gray-500">
                                  Cliquez sur l'icône caméra en haut à droite ou sélectionnez une nouvelle image ci-dessus pour changer votre photo de profil.
                                </p>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  Vous n'avez pas encore de photo de profil. Sélectionnez une image ci-dessus pour en ajouter une.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.fullName()}
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        disabled={!isEditing}
                        maxLength={50}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.email()}
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          disabled={!isEditing}
                          className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.phone()}
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          disabled={!isEditing}
                          className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.address()}
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                        <input
                          type="text"
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          disabled={!isEditing}
                          className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.city()}
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => handleInputChange("city", e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.country()}
                      </label>
                      <input
                        type="text"
                        value={formData.country}
                        onChange={(e) => handleInputChange("country", e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* Password Tab */}
            {activeTab === "password" && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {tObj.profile.passwordManagement()}
                  </h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.currentPassword()}
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) => handlePasswordChange("currentPassword", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={tObj.profile.enterCurrentPassword()}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.newPassword()}
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={tObj.profile.enterNewPassword()}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {tObj.profile.confirmNewPassword()}
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder={tObj.profile.confirmNewPasswordPlaceholder()}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={handlePasswordUpdate}
                        disabled={passwordLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                        className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {passwordLoading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <Key className="w-4 h-4 mr-2" />
                        )}
                        {passwordLoading ? tObj.profile.updating() : tObj.profile.updatePassword()}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <Lock className="h-5 w-5 text-yellow-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        {tObj.profile.passwordSecurity()}
                      </h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <ul className="list-disc pl-5 space-y-1">
                          <li>{tObj.profile.passwordSecurityDesc1()}</li>
                          <li>{tObj.profile.passwordSecurityDesc2()}</li>
                          <li>{tObj.profile.passwordSecurityDesc3()}</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Courses Tab - Seulement pour les étudiants */}
            {activeTab === "courses" && user?.role?.toLowerCase() === 'student' && !user?.isAdmin && (
              <div className="space-y-6">
                {/* Learning Stats */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {tObj.profile.learningStats()}
                  </h2>
                  <div className="grid grid-cols-3 gap-3 sm:gap-4 md:gap-6">
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-blue-600 mb-1">
                        {userStats.totalCourses}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">{tObj.profile.enrolledCoursesCount()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-orange-600 mb-1">
                        {userStats.inProgressCourses}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">{tObj.profile.inProgressCourses()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl sm:text-2xl font-bold text-green-600 mb-1">
                        {userStats.completedCourses}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">{tObj.profile.completedCourses()}</div>
                    </div>
                  </div>
                </div>

                {/* My Courses Section */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6">
                    {tObj.profile.myCourses()}
                  </h2>
                  <MyCoursesSection />
                </div>
              </div>
            )}

            {/* Certificates Tab - Pour les étudiants et les admins */}
            {activeTab === "achievements" && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">
                  {tObj.profile.myCertificates()}
                </h2>
                {certificatesLoading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(3)].map((_, i) => (
                      <CertificateSkeleton key={i} />
                    ))}
                  </div>
                ) : certificates.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {certificates.map((certificate) => {
                      // Préparer les données pour CertificatePreview
                      const certificateData = {
                        ...certificate,
                        courseId: certificate.course?.id
                      };
                      
                      return (
                        <CertificatePreview
                          key={certificate.id}
                          certificate={certificateData}
                          onDownload={handleDownloadCertificate}
                          showEvaluationModal={false}
                          onEvaluationComplete={() => {
                            // Recharger les certificats après évaluation
                            // Le hook useCertificates devrait automatiquement recharger
                            window.location.reload();
                          }}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{tObj.profile.noCertificates()}</h3>
                    <p className="text-gray-500 mb-6">{tObj.profile.noCertificatesDesc()}</p>
                    <button 
                      onClick={() => navigate('/courses')}
                      className="px-6 py-3 bg-[#203a8a] text-white rounded-lg hover:bg-[#1a1f4a]"
                    >
                      {tObj.profile.discoverCourses()}
                    </button>
                  </div>
                )}
              </div>
            )}


            {/* Admin Tab - Seulement pour les administrateurs */}
            {activeTab === "admin" && (user?.role?.toLowerCase() === 'admin' || user?.isAdmin) && (
              <div className="space-y-6">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    {tObj.profile.administration()}
                  </h2>
                  <p className="text-gray-600 mb-6">
                    {tObj.profile.adminAccessDesc()}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <button 
                      onClick={() => navigate('/admin/dashboard')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <BarChart3 className="w-8 h-8 text-blue-600 mr-3" />
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">{tObj.profile.adminDashboard()}</h3>
                        <p className="text-sm text-gray-500">{tObj.profile.adminDashboardDesc()}</p>
                      </div>
                    </button>
                    <button 
                      onClick={() => navigate('/admin/users')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <Users className="w-8 h-8 text-green-600 mr-3" />
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">{tObj.profile.adminUsers()}</h3>
                        <p className="text-sm text-gray-500">{tObj.profile.adminUsersDesc()}</p>
                      </div>
                    </button>
                    <button 
                      onClick={() => navigate('/admin/courses')}
                      className="flex items-center p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
                    >
                      <BookOpen className="w-8 h-8 text-purple-600 mr-3" />
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">{tObj.profile.adminCourses()}</h3>
                        <p className="text-sm text-gray-500">{tObj.profile.adminCoursesDesc()}</p>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  )
}
